import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import PlayerNameWithFlag from "../players/PlayerNameWithFlag";
import { formatDate } from "../../util/StringUtils";
import { getLadderColor, getLadderName } from "../../util/HelperUtils";
import i18n from "../../util/i18n";
import { TOURNAMENT_STATES } from "../../util/Constants";
import type { Tournament } from "../../types/domain";

interface TournamentCardProps {
  tournament: Tournament;
}

function TournamentCard({ tournament }: TournamentCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleOnClick = useCallback(() => navigate('/tournaments/' + tournament.id), [navigate, tournament.id]);

  const ladderColor = getLadderColor(tournament.ladder?.id);

  return (
    <div
      className="tournament-card clickable"
      onClick={handleOnClick}
      style={{ borderLeftColor: ladderColor }}
    >
      <div className="tournament-card-header">
        <span className="tournament-card-ladder" style={{ color: ladderColor }}>
          {tournament.ladder ? getLadderName(i18n, tournament.ladder) : '-'}
        </span>
        <span className="tournament-card-date">{formatDate(tournament.createdAt)}</span>
      </div>

      <h3 className="tournament-card-title">{tournament.name}</h3>

      <div className="tournament-card-info">
        <div className="tournament-card-info-item">
          <span className="tournament-card-label">{t("players")}</span>
          <span className="tournament-card-value">{tournament.numberOfPlayers}</span>
        </div>
        <div className="tournament-card-info-item">
          <span className="tournament-card-label">{t("tournamentSystem")}</span>
          <span className="tournament-card-value">{t(`tournamentSystem_${tournament.type}`)}</span>
        </div>
        <div className="tournament-card-info-item">
          <span className="tournament-card-label">{t("setsToWin")}</span>
          <span className="tournament-card-value">{tournament.setsNeededToWin}</span>
        </div>
        <div className="tournament-card-info-item">
          <span className="tournament-card-label">{t("averageElo")}</span>
          <span className="tournament-card-value">{tournament.averageElo ?? '-'}</span>
        </div>
      </div>

      {tournament.state === TOURNAMENT_STATES.FINISHED && tournament.firstPlace && (
        <div className="tournament-card-podium">
          <div className="tournament-card-winner">
            <img src="/icons/gold-medal.png" alt="1st" className="tournament-card-medal" />
            <PlayerNameWithFlag player={tournament.firstPlace} notLink={true} />
          </div>
          {tournament.secondPlace && (
            <div className="tournament-card-runner-up">
              <img src="/icons/silver-medal.png" alt="2nd" className="tournament-card-medal" />
              <PlayerNameWithFlag player={tournament.secondPlace} notLink={true} />
            </div>
          )}
          {tournament.thirdPlace && (
            <div className="tournament-card-runner-up hide-on-mobile">
              <img src="/icons/bronze-medal.png" alt="3rd" className="tournament-card-medal" />
              <PlayerNameWithFlag player={tournament.thirdPlace} notLink={true} />
            </div>
          )}
        </div>
      )}

      {tournament.state === TOURNAMENT_STATES.IN_PROGRESS && (
        <div className="tournament-card-status">
          <span className="tournament-card-live-indicator"></span>
          {t("tournamentState_IN_PROGRESS")}
        </div>
      )}
    </div>
  );
}

export default TournamentCard;
